import type { LocalizedString } from "@repo/shared/types";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LocalizedInput } from "@/components/ui/localized-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useT } from "@/lib/i18n/use-t";
import { newOption, type OptionDraft, type QuestionDraft } from "./assessment-drafts";
import { T } from "./question-edit-dialog.i18n";

/** QL-253 — the fixed "None + Category 1-10" slots; never custom-named. */
const CATEGORY_SLOTS = Array.from({ length: 10 }, (_, i) => i + 1);

type QuestionEditDialogProps = {
  question: QuestionDraft | null;
  /** 0-based position in the pool, used for the "Question N" labels. */
  index: number;
  supportedLanguages: string[];
  disabled: boolean;
  /** QL-253 — category grouping only affects the QUIZ mode's draw. */
  showCategory: boolean;
  onChange: (patch: Partial<QuestionDraft>) => void;
  onChangeOption: (optionId: string, patch: Partial<OptionDraft>) => void;
  onClose: () => void;
};

/**
 * Per-question editor, nested inside the assessment dialog so the outer dialog
 * stays a fixed height regardless of how many questions the pool holds.
 */
export function QuestionEditDialog({
  question,
  index,
  supportedLanguages,
  disabled,
  showCategory,
  onChange,
  onChangeOption,
  onClose,
}: QuestionEditDialogProps) {
  const t = useT(T);
  return (
    <Dialog
      open={question != null}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t.editQuestion}
            {index >= 0 ? ` ${index + 1}` : ""}
          </DialogTitle>
          <DialogDescription>{t.editQuestionHint}</DialogDescription>
        </DialogHeader>

        {question && (
          <div className="space-y-3 py-2">
            {showCategory && (
              <div className="space-y-2">
                <Label htmlFor={`category-${question.id}`}>{t.category}</Label>
                <Select
                  value={question.category == null ? "none" : String(question.category)}
                  onValueChange={(v) => onChange({ category: v === "none" ? null : Number(v) })}
                >
                  <SelectTrigger
                    id={`category-${question.id}`}
                    className="w-40"
                    disabled={disabled}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t.categoryNone}</SelectItem>
                    {CATEGORY_SLOTS.map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {`${t.category} ${n}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t.categoryHint}</p>
              </div>
            )}

            <LocalizedInput
              id={`question-${question.id}`}
              label={`${t.question} ${index + 1}`}
              value={question.label}
              i18nValue={question.i18n as LocalizedString}
              onChange={(value) => onChange({ label: value })}
              onI18nChange={(i18n) => onChange({ i18n })}
              supportedLanguages={supportedLanguages}
              disabled={disabled}
              required
            />

            {question.options.map((option, oIndex) => (
              <div key={option.id} className="flex items-end gap-2">
                <div className="flex-1">
                  <LocalizedInput
                    id={`option-${option.id}`}
                    label={`${t.option} ${oIndex + 1}`}
                    value={option.label}
                    i18nValue={option.i18n as LocalizedString}
                    onChange={(value) => onChangeOption(option.id, { label: value })}
                    onI18nChange={(i18n) => onChangeOption(option.id, { i18n })}
                    supportedLanguages={supportedLanguages}
                    disabled={disabled}
                    required
                  />
                </div>
                <div className="w-24 space-y-2">
                  <Label htmlFor={`option-score-${option.id}`}>{t.score}</Label>
                  <Input
                    id={`option-score-${option.id}`}
                    type="number"
                    step="any"
                    value={option.score}
                    onChange={(e) => onChangeOption(option.id, { score: e.target.value })}
                    disabled={disabled}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  tooltip={t.removeOption}
                  className="shrink-0"
                  onClick={() =>
                    onChange({ options: question.options.filter((o) => o.id !== option.id) })
                  }
                  disabled={disabled || question.options.length <= 2}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">{t.optionsMinHint}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onChange({ options: [...question.options, newOption()] })}
                disabled={disabled}
              >
                <Plus className="h-4 w-4 mr-1" />
                {t.addOption}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" onClick={onClose}>
            {t.done}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
